import type { RequestArchiveReason } from "@nlc/shared-types";

import { ARCHIVE_REASON_LABELS, ratingLanes } from "@nlc/shared-types";
import { AnimatePresence, motion } from "framer-motion";
import { PencilIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogTitle,
} from "@/components/dialog";
import { Modal } from "@/components/modal";
import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { RatingLane } from "@/features/request/edit/components/rating-modal/RatingLane";
import { useRatingLanes } from "@/features/request/edit/components/rating-modal/useRatingLanes";
import { dateFormatter } from "@/utils/dateFormatter";

import { useGetRequestRating } from "../hooks/useGetRequestRating";
import { useUpdateRequestRating } from "../hooks/useUpdateRequestRating";
import { RatingAssigneePicker } from "./RatingAssigneePicker";
import { RatingEditHistory } from "./RatingEditHistory";
import { SWAP_TRANSITION } from "./ratingMotion";
import { RatingUser } from "./RatingUser";

interface RatingViewModalProps {
  archiveComment?: null | string;
  archiveReason?: null | string;
  initialEditing?: boolean;
  isOpen: boolean;
  requestId: null | number;
  setIsOpen: (isOpen: boolean) => void;
}

export function RatingViewModal({
  archiveComment,
  archiveReason,
  initialEditing = false,
  isOpen,
  requestId,
  setIsOpen,
}: RatingViewModalProps) {
  const { canAccess } = useRoleAccess();
  const { data, isError, isLoading } = useGetRequestRating(
    isOpen ? requestId : null,
  );
  const { isPending, updateRating } = useUpdateRequestRating(requestId);

  const [isEditing, setIsEditing] = useState(initialEditing);
  const { lanes, setLanes, setStars, toggleChip } = useRatingLanes();
  const [comment, setComment] = useState("");
  const [designerId, setDesignerId] = useState<null | number>(null);

  const rating = data?.rating;
  const lastEdit = data?.edits[0];
  const canEdit = canAccess("request.rating.edit");
  const archiveLabel = archiveReason
    ? (ARCHIVE_REASON_LABELS[archiveReason as RequestArchiveReason] ??
      archiveReason)
    : null;

  const resetFromRating = useCallback(() => {
    if (!rating) return;
    setLanes({
      design: { chips: rating.designChips, stars: rating.design },
      platform: { chips: rating.platformChips, stars: rating.platform },
      team: { chips: rating.teamChips, stars: rating.team },
    });
    setComment(rating.comment ?? "");
    setDesignerId(rating.designer?.id ?? null);
  }, [rating, setLanes]);

  useEffect(() => {
    resetFromRating();
  }, [resetFromRating]);

  useEffect(() => {
    if (!isOpen) setIsEditing(initialEditing);
  }, [initialEditing, isOpen]);

  const handleSave = () => {
    if (!data) return;

    updateRating(
      {
        comment: comment || undefined,
        design: lanes.design.stars,
        designChips: lanes.design.chips,
        designerId,
        expectedEditCount: data.edits.length,
        platform: lanes.platform.stars,
        platformChips: lanes.platform.chips,
        team: lanes.team.stars,
        teamChips: lanes.team.chips,
      },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  return (
    <Modal
      classNames={{ modal: "w-[640px] max-h-[85vh]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <Dialog>
        <div className="absolute end-[12px] top-[12px] z-10 flex items-center gap-xs">
          {canEdit && !isEditing && rating && (
            <Button
              onPress={() => setIsEditing(true)}
              size="sm"
              variant="tertiary"
            >
              Edit
            </Button>
          )}
          <DialogCloseButton className="static p-[10px]" />
        </div>

        <div className="shrink-0 border-b border-border-secondary px-[24px] pb-md pr-[8rem] pt-[24px]">
          <DialogTitle className="text-xl font-semibold text-text-primary">
            How did this project turn out?
          </DialogTitle>
          {rating && (
            <p className="mt-xs flex flex-wrap items-center gap-xs text-sm text-text-tertiary">
              Submitted by{" "}
              <RatingUser
                avatar={rating.reviewer?.avatar}
                fallback="the client"
                name={rating.reviewer?.name ?? null}
              />{" "}
              on {dateFormatter(rating.submittedAt)}
            </p>
          )}
          {lastEdit && (
            <p className="mt-xs flex flex-wrap items-center gap-xs text-sm text-text-tertiary">
              Last edited by{" "}
              <RatingUser
                avatar={lastEdit.editedBy?.avatar}
                fallback="a teammate"
                name={lastEdit.editedBy?.name ?? null}
              />{" "}
              on {dateFormatter(lastEdit.editedAt)}
            </p>
          )}
        </div>

        <DialogBody className="max-h-[75vh] pb-3xl">
          <div className="flex flex-col gap-lg">
            {isLoading && (
              <p className="text-sm text-text-tertiary">Loading rating...</p>
            )}

            {isError && (
              <p className="text-sm text-text-error">
                Failed to load this rating. Please try again later.
              </p>
            )}

            {rating && (
              <>
                {ratingLanes.map((lane) => (
                  <RatingLane
                    chips={lanes[lane].chips}
                    isDisabled={!isEditing}
                    key={lane}
                    lane={lane}
                    onStarsChange={(stars) => setStars(lane, stars)}
                    onToggleChip={(chip) => toggleChip(lane, chip)}
                    selectedOnly={!isEditing}
                    stars={lanes[lane].stars}
                  />
                ))}

                <div className="flex flex-col gap-xs">
                  <p className="text-md font-semibold text-text-primary">
                    Anything else on your mind?
                  </p>
                  <div className="relative">
                    <textarea
                      className="w-full resize-none rounded-lg border border-border-primary bg-bg-primary p-md pb-xl text-sm text-text-primary outline-hidden focus-visible:border-border-brand-solid disabled:bg-bg-secondary"
                      disabled={!isEditing}
                      maxLength={1000}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder="No comment was left."
                      rows={3}
                      value={comment}
                    />
                    <AnimatePresence>
                      {isEditing && (
                        <motion.span
                          animate={{ opacity: 1 }}
                          className="pointer-events-none absolute bottom-md right-md text-xs text-text-tertiary"
                          exit={{ opacity: 0 }}
                          initial={{ opacity: 0 }}
                          transition={SWAP_TRANSITION}
                        >
                          {comment.length}/1000
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                <motion.div
                  animate={{ opacity: 1, y: 0 }}
                  initial={{ opacity: 0, y: -6 }}
                  key={isEditing ? "assignee-edit" : "assignee-read"}
                  transition={SWAP_TRANSITION}
                >
                  {isEditing ? (
                    <RatingAssigneePicker
                      onChange={setDesignerId}
                      participants={data.participants}
                      requestId={rating.requestId}
                      selectedAvatar={rating.designer?.avatar}
                      selectedName={rating.designer?.name ?? null}
                      value={designerId}
                    />
                  ) : (
                    <div className="flex flex-col items-start gap-sm">
                      <p className="text-md font-semibold text-text-primary">
                        Rating assigned to
                      </p>
                      {canEdit ? (
                        <Button
                          className="flex items-center gap-xs text-sm text-text-tertiary underline decoration-dotted underline-offset-4 hover:text-text-primary"
                          onPress={() => setIsEditing(true)}
                          variant="unstyled"
                        >
                          <RatingUser
                            avatar={rating.designer?.avatar}
                            name={rating.designer?.name ?? null}
                          />
                          <PencilIcon className="size-3.5" />
                        </Button>
                      ) : (
                        <RatingUser
                          avatar={rating.designer?.avatar}
                          className="text-sm text-text-tertiary"
                          name={rating.designer?.name ?? null}
                        />
                      )}
                    </div>
                  )}
                </motion.div>

                <RatingEditHistory edits={data.edits} />

                {archiveLabel && (
                  <div className="flex flex-col gap-xs border-t border-border-secondary pt-md">
                    <p className="text-sm font-semibold text-text-primary">
                      Previously archived
                    </p>
                    <p className="text-sm text-text-tertiary">
                      {archiveLabel}
                      {archiveComment ? `: ${archiveComment}` : ""}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogBody>

        <AnimatePresence>
          {isEditing && (
            <motion.div
              animate={{ height: "auto", opacity: 1 }}
              className="shrink-0"
              exit={{ height: 0, opacity: 0 }}
              initial={{ height: 0, opacity: 0 }}
              style={{ overflow: "hidden" }}
              transition={SWAP_TRANSITION}
            >
              <DialogFooter>
                <Button
                  className="w-full"
                  isDisabled={isPending}
                  onPress={handleSave}
                  size="lg"
                  variant="primary"
                >
                  {isPending ? "Saving..." : "Save changes"}
                </Button>
                <Button
                  color="gray"
                  isDisabled={isPending}
                  onPress={() => {
                    resetFromRating();
                    setIsEditing(false);
                  }}
                  size="lg"
                  variant="tertiary"
                >
                  Cancel
                </Button>
              </DialogFooter>
            </motion.div>
          )}
        </AnimatePresence>
      </Dialog>
    </Modal>
  );
}
